import React, { useState } from 'react';

import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faChevronDown, faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { Button } from 'components/ui/button';
import { Collapsible } from '@buildoutinc/blueprint-react/ui/Collapsible';
import { Offcanvas } from '@buildoutinc/blueprint-react/ui/Offcanvas';
import { Separator } from '@buildoutinc/blueprint-react/ui/Separator';

import { navIcon } from './icons';
import { matchesPath, navItemState } from './paths';
import { openSupport } from './support';
import type { OsNavItem, OsNavLogo } from './types';

interface LinkProps {
  item: OsNavItem;
  currentPath: string;
}

function MobileLink({ item, currentPath }: LinkProps) {
  const isActive = matchesPath(item.path, currentPath);

  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={item.disabled || undefined}
      className={classNames('sidebar-menu-link', { active: isActive, disabled: item.disabled })}
      href={item.path}
    >
      <span className="sidebar-menu-link-icon">
        <FontAwesomeIcon icon={navIcon(item.key, isActive)!} />
      </span>
      <span className="sidebar-menu-link-label">{item.label}</span>
    </a>
  );
}

function MobileGroup({ item, currentPath }: LinkProps) {
  const { isActive } = navItemState(item, currentPath);
  const [isOpen, setIsOpen] = useState(isActive);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Collapsible.Trigger className={classNames('sidebar-menu-link', { active: isActive })}>
        <span className="sidebar-menu-link-icon">
          <FontAwesomeIcon icon={navIcon(item.key, isActive)!} />
        </span>
        <span className="sidebar-menu-link-label">{item.label}</span>
        <span className="sidebar-menu-link-icon os-nav__panel-caret">
          <FontAwesomeIcon icon={faChevronDown} />
        </span>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <div className="sidebar-group-menu">
          {item.children?.map(child => (
            <a
              aria-current={matchesPath(child.path, currentPath) ? 'page' : undefined}
              aria-disabled={child.disabled || undefined}
              className={classNames('sidebar-group-item', {
                active: matchesPath(child.path, currentPath),
                disabled: child.disabled,
              })}
              href={child.path}
              key={child.key}
            >
              {child.label}
            </a>
          ))}
        </div>
      </Collapsible.Content>
    </Collapsible>
  );
}

interface MobileMenuProps {
  items: OsNavItem[];
  tasks?: OsNavItem;
  currentPath: string;
  logo: OsNavLogo;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function MobileMenu({
  items,
  tasks,
  currentPath,
  logo,
  isOpen,
  onOpenChange,
}: MobileMenuProps) {
  return (
    <Offcanvas open={isOpen} onOpenChange={onOpenChange}>
      <Offcanvas.Content className="os-nav__panel" id="os-nav-menu" side="left">
        <Offcanvas.Header className="os-nav__panel-header">
          <img alt="Buildout" className="os-nav__panel-logo" src={logo.src} />
          <Button
            aria-label="Notifications"
            className="os-nav__action ms-auto"
            disabled
            icon={faBell}
            variant="ghost"
          />
        </Offcanvas.Header>

        <Offcanvas.Body className="os-nav__panel-body">
          {tasks && (
            <>
              <MobileLink currentPath={currentPath} item={tasks} />
              <Separator />
            </>
          )}

          {items.map(item => item.children?.length ? (
            <MobileGroup currentPath={currentPath} item={item} key={item.key} />
          ) : (
            <MobileLink currentPath={currentPath} item={item} key={item.key} />
          )
          )}

          <Separator />

          <button
            className="sidebar-menu-link"
            type="button"
            onClick={() => {
              onOpenChange(false);
              openSupport();
            }}
          >
            <FontAwesomeIcon fixedWidth icon={faCircleQuestion} />
            Support
          </button>
        </Offcanvas.Body>
      </Offcanvas.Content>
    </Offcanvas>
  );
}
